import React, { useState, useRef, useEffect } from 'react';

function App() {
  const [prompt, setPrompt] = useState('');
  const [negativePrompt, setNegativePrompt] = useState('');
  const [width, setWidth] = useState(1024);
  const [height, setHeight] = useState(1024);
  const [guidanceScale, setGuidanceScale] = useState(3);
  const [useNegativePrompt, setUseNegativePrompt] = useState(false);
  const [numImages, setNumImages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [messages, setMessages] = useState([]);
  const [error, setError] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleGenerate = async () => {
    if (!prompt.trim()) return;

    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: prompt,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setLoading(true);
    setError(null);
    
    const currentPrompt = prompt;
    setPrompt('');

    try {
      // Generate multiple images if requested
      const imagePromises = Array.from({ length: numImages }, async () => {
        const response = await fetch('https://apinc.technologyinnovision.com/generate-image', {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'Accept': 'application/json',
          },
          mode: 'cors',
          body: JSON.stringify({
            prompt: currentPrompt,
            negative_prompt: negativePrompt,
            width,
            height,
            guidance_scale: guidanceScale,
            use_negative_prompt: useNegativePrompt,
          }),
        });
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        return response.json();
      });

      const results = await Promise.all(imagePromises);
      const successfulImages = results.filter(data => data.success && data.image_url);
      
      if (successfulImages.length > 0) {
        const botMessage = {
          id: Date.now() + 1,
          type: 'bot',
          content: currentPrompt,
          images: successfulImages.map(data => data.image_url),
          timestamp: new Date()
        };
        setMessages(prev => [...prev, botMessage]);
      } else {
        const errorMessage = results[0]?.message || 'Failed to generate image.';
        setError(errorMessage);
        const botMessage = {
          id: Date.now() + 1,
          type: 'error',
          content: errorMessage,
          timestamp: new Date()
        };
        setMessages(prev => [...prev, botMessage]);
      }
    } catch (e) {
      const errorMsg = `Error: ${e.message}`;
      setError(errorMsg);
      const botMessage = {
        id: Date.now() + 1,
        type: 'error',
        content: errorMsg,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, botMessage]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleGenerate();
    }
  };

  const adjustTextareaHeight = () => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = Math.min(textarea.scrollHeight, 200) + 'px';
    }
  };

  useEffect(() => {
    adjustTextareaHeight();
  }, [prompt]);

  return (
    <div className="min-h-screen bg-black text-white overflow-hidden">
      {/* Animated Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-purple-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-blue-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse" style={{animationDelay: '2s'}}></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-80 h-80 bg-pink-500 rounded-full mix-blend-multiply filter blur-xl opacity-10 animate-pulse" style={{animationDelay: '4s'}}></div>
      </div>

      {/* Header */}
      <header className="relative z-10 backdrop-blur-xl bg-black/80 border-b border-gray-800/50 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="w-10 h-10 bg-gradient-to-br from-purple-400 via-pink-400 to-blue-400 rounded-xl flex items-center justify-center">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
            <div>
              <h1 className="text-2xl font-bold bg-gradient-to-r from-purple-400 via-pink-400 to-blue-400 bg-clip-text text-transparent">
                NeuralImagen
              </h1>
              <p className="text-sm text-gray-400">Next-Gen AI Image Studio</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-3">
            <button
              onClick={() => setShowSettings(!showSettings)}
              className="relative p-3 rounded-2xl bg-gray-900/50 backdrop-blur-sm border border-gray-800/50 hover:border-purple-500/50 transition-all duration-300 group"
            >
              <svg className={`w-5 h-5 transition-transform duration-300 ${showSettings ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-purple-500/20 to-pink-500/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            </button>
          </div>
        </div>
      </header>

      {/* Ultra Modern Settings Panel */}
      <div className={`relative z-10 transition-all duration-500 ease-out ${showSettings ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'} overflow-hidden`}>
        <div className="backdrop-blur-xl bg-gray-900/30 border-b border-gray-800/30 p-6">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6 gap-6">
              {/* Dimensions */}
              <div className="space-y-3">
                <label className="block text-sm font-medium text-gray-300">Dimensions</label>
                <div className="grid grid-cols-2 gap-2">
                  <div className="relative">
                    <input
                      type="number"
                      min={256}
                      max={2048}
                      step={64}
                      value={width}
                      onChange={e => setWidth(Number(e.target.value))}
                      className="w-full px-3 py-2 bg-gray-900/50 backdrop-blur-sm border border-gray-700/50 rounded-xl text-white text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500/50 transition-all duration-300"
                      placeholder="W"
                    />
                  </div>
                  <div className="relative">
                    <input
                      type="number"
                      min={256}
                      max={2048}
                      step={64}
                      value={height}
                      onChange={e => setHeight(Number(e.target.value))}
                      className="w-full px-3 py-2 bg-gray-900/50 backdrop-blur-sm border border-gray-700/50 rounded-xl text-white text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500/50 transition-all duration-300"
                      placeholder="H"
                    />
                  </div>
                </div>
              </div>

              {/* Guidance Scale */}
              <div className="space-y-3">
                <label className="block text-sm font-medium text-gray-300">
                  Guidance <span className="text-purple-400 font-semibold">{guidanceScale}</span>
                </label>
                <div className="relative">
                  <input
                    type="range"
                    min={1}
                    max={20}
                    step={0.1}
                    value={guidanceScale}
                    onChange={e => setGuidanceScale(Number(e.target.value))}
                    className="w-full h-2 bg-gray-700/50 rounded-full appearance-none cursor-pointer slider-modern"
                  />
                </div>
              </div>

              {/* Number of Images */}
              <div className="space-y-3">
                <label className="block text-sm font-medium text-gray-300">Images</label>
                <select
                  value={numImages}
                  onChange={e => setNumImages(Number(e.target.value))}
                  className="w-full px-3 py-2 bg-gray-900/50 backdrop-blur-sm border border-gray-700/50 rounded-xl text-white text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500/50 transition-all duration-300"
                >
                  <option value={1}>1 Image</option>
                  <option value={2}>2 Images</option>
                  <option value={3}>3 Images</option>
                  <option value={4}>4 Images</option>
                </select>
              </div>

              {/* Negative Prompt Toggle */}
              <div className="space-y-3">
                <label className="block text-sm font-medium text-gray-300">Negative Prompt</label>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={useNegativePrompt}
                    onChange={e => setUseNegativePrompt(e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="relative w-11 h-6 bg-gray-700/50 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-500/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-gradient-to-r peer-checked:from-purple-500 peer-checked:to-pink-500"></div>
                </label>
              </div>

              {/* Negative Prompt Input */}
              {useNegativePrompt && (
                <div className="md:col-span-2 space-y-3">
                  <label className="block text-sm font-medium text-gray-300">What to avoid</label>
                  <input
                    type="text"
                    value={negativePrompt}
                    onChange={e => setNegativePrompt(e.target.value)}
                    placeholder="blurry, low quality, distorted..."
                    className="w-full px-3 py-2 bg-gray-900/50 backdrop-blur-sm border border-gray-700/50 rounded-xl text-white text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500/50 transition-all duration-300"
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Messages Area */}
      <div className="relative z-10 flex-1 overflow-y-auto px-6 py-8" style={{height: 'calc(100vh - 200px)'}}>
        <div className="max-w-5xl mx-auto space-y-8">
          {messages.length === 0 && (
            <div className="text-center py-20">
              <div className="relative mx-auto mb-8">
                <div className="w-24 h-24 mx-auto bg-gradient-to-br from-purple-400 via-pink-400 to-blue-400 rounded-full flex items-center justify-center animate-pulse">
                  <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
                <div className="absolute inset-0 w-24 h-24 mx-auto bg-gradient-to-br from-purple-400 via-pink-400 to-blue-400 rounded-full animate-ping opacity-20"></div>
              </div>
              <h2 className="text-4xl font-bold bg-gradient-to-r from-purple-400 via-pink-400 to-blue-400 bg-clip-text text-transparent mb-4">
                Ready to Create Magic?
              </h2>
              <p className="text-xl text-gray-400 max-w-2xl mx-auto leading-relaxed">
                Describe your vision and watch as AI transforms your words into stunning visual masterpieces
              </p>
            </div>
          )}

          {messages.map((message, index) => (
            <div key={message.id} className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'} animate-in slide-in-from-bottom-4 duration-500`} style={{animationDelay: `${index * 100}ms`}}>
              <div className={`max-w-4xl relative ${
                message.type === 'user' 
                  ? 'bg-gradient-to-br from-purple-500 to-pink-500' 
                  : message.type === 'error' 
                    ? 'bg-gradient-to-br from-red-500 to-orange-500' 
                    : 'bg-gray-900/40 backdrop-blur-xl border border-gray-800/50'
              } rounded-3xl p-6 shadow-2xl`}>
                {message.type === 'user' ? (
                  <p className="text-white text-lg">{message.content}</p>
                ) : message.type === 'error' ? (
                  <div className="flex items-center text-white">
                    <svg className="w-6 h-6 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p className="text-lg">{message.content}</p>
                  </div>
                ) : (
                  <div>
                    <p className="text-gray-300 mb-6 text-lg font-medium">"{message.content}"</p>
                    <div className={`grid gap-4 ${
                      message.images.length === 1 ? 'grid-cols-1' : 
                      message.images.length === 2 ? 'grid-cols-1 md:grid-cols-2' : 
                      'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'
                    }`}>
                      {message.images.map((imageUrl, imgIndex) => (
                        <div key={imgIndex} className="relative group overflow-hidden rounded-2xl bg-gray-800/50 backdrop-blur-sm">
                          <img
                            src={imageUrl}
                            alt={`Generated ${imgIndex + 1}`}
                            className="w-full h-auto transition-all duration-700 group-hover:scale-110"
                            style={{aspectRatio: `${width}/${height}`}}
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-all duration-300 flex items-end justify-center pb-6">
                            <a
                              href={imageUrl}
                              download
                              className="bg-white/90 backdrop-blur-sm text-gray-900 px-6 py-3 rounded-2xl font-semibold hover:bg-white transition-all duration-300 transform translate-y-4 group-hover:translate-y-0"
                            >
                              Download HD
                            </a>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}

          {loading && (
            <div className="flex justify-start animate-in slide-in-from-bottom-4 duration-500">
              <div className="bg-gray-900/40 backdrop-blur-xl border border-gray-800/50 rounded-3xl p-6 max-w-4xl shadow-2xl">
                <div className="flex items-center space-x-4 mb-6">
                  <div className="flex space-x-2">
                    <div className="w-3 h-3 bg-purple-400 rounded-full animate-bounce"></div>
                    <div className="w-3 h-3 bg-pink-400 rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
                    <div className="w-3 h-3 bg-blue-400 rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
                  </div>
                  <span className="text-gray-300 text-lg font-medium">Creating {numImages} masterpiece{numImages > 1 ? 's' : ''}...</span>
                </div>
                <div className={`grid gap-4 ${
                  numImages === 1 ? 'grid-cols-1' : 
                  numImages === 2 ? 'grid-cols-1 md:grid-cols-2' : 
                  'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'
                }`}>
                  {Array.from({ length: numImages }).map((_, index) => (
                    <div key={index} className="relative overflow-hidden rounded-2xl bg-gray-800/30 animate-pulse" style={{aspectRatio: `${width}/${height}`}}>
                      <div className="absolute inset-0 bg-gradient-to-br from-purple-500/20 via-pink-500/20 to-blue-500/20"></div>
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent animate-shimmer"></div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Ultra Modern Input Area */}
      <div className="relative z-10 backdrop-blur-xl bg-black/60 border-t border-gray-800/30 px-6 py-6">
        <div className="max-w-5xl mx-auto">
          <div className="relative">
            <div className="relative flex items-end space-x-4">
              <div className="flex-1 relative">
                <textarea
                  ref={textareaRef}
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Describe your vision in vivid detail..."
                  className="w-full px-6 py-4 pr-16 bg-gray-900/50 backdrop-blur-sm border border-gray-700/50 rounded-3xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500/50 resize-none overflow-hidden min-h-[60px] max-h-[200px] text-lg transition-all duration-300"
                  rows={1}
                />
                <div className="absolute inset-0 rounded-3xl bg-gradient-to-r from-purple-500/10 via-pink-500/10 to-blue-500/10 opacity-0 focus-within:opacity-100 transition-opacity duration-300 pointer-events-none"></div>
              </div>
              <button
                onClick={handleGenerate}
                disabled={loading || !prompt.trim()}
                className="relative p-4 bg-gradient-to-br from-purple-500 via-pink-500 to-blue-500 text-white rounded-3xl hover:shadow-2xl hover:shadow-purple-500/25 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 flex-shrink-0 group overflow-hidden"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-purple-400 via-pink-400 to-blue-400 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <div className="relative z-10">
                  {loading ? (
                    <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  ) : (
                    <svg className="w-6 h-6 transition-transform duration-300 group-hover:scale-110" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                    </svg>
                  )}
                </div>
              </button>
            </div>
          </div>
        </div>
      </div>

      <style dangerouslySetInnerHTML={{
        __html: `
          .slider-modern::-webkit-slider-thumb {
            appearance: none;
            height: 20px;
            width: 20px;
            border-radius: 50%;
            background: linear-gradient(135deg, #a855f7, #ec4899);
            cursor: pointer;
            box-shadow: 0 0 20px rgba(168, 85, 247, 0.3);
            transition: all 0.3s ease;
          }
          .slider-modern::-webkit-slider-thumb:hover {
            transform: scale(1.2);
            box-shadow: 0 0 30px rgba(168, 85, 247, 0.5);
          }
          .slider-modern::-moz-range-thumb {
            height: 20px;
            width: 20px;
            border-radius: 50%;
            background: linear-gradient(135deg, #a855f7, #ec4899);
            cursor: pointer;
            border: none;
            box-shadow: 0 0 20px rgba(168, 85, 247, 0.3);
          }
          @keyframes shimmer {
            0% { transform: translateX(-100%); }
            100% { transform: translateX(100%); }
          }
          .animate-shimmer {
            animation: shimmer 2s infinite;
          }
          @keyframes animate-in {
            from {
              opacity: 0;
              transform: translateY(10px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          .animate-in {
            animation: animate-in 0.5s ease-out;
          }
        `
      }} />
    </div>
  );
}

export default App;